import React, { useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router";

import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import CompareIcon from "@mui/icons-material/Compare";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import { Box, CircularProgress, IconButton, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ReactComponent as Logo } from "assets/icons/logo.svg";
import {
  GlobalWorkspaceSidebarIcon,
  PortfolioOverviewIcon,
  PsychologyReverse,
  ReportGeneratorIcon
} from "assets/material-custom-icons";
import { RoleComponentGuard } from "components";
import { AccountInvite } from "components/Account-sign-up";
import { UpgradePlanModal } from "components/modals";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { RoleName } from "constants/index";
import { Routes } from "constants/routes";
import { useNavigate } from "hooks";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import {
  useAccountSelector,
  useAuthActions,
  useBenchmarkSelector,
  useInvestorsSelector,
  usePortfolioConstructSelector,
  useReportsSelector,
  useSidebarStateSelect,
  useUserPermissions,
  useUserRoleSelector,
  useUserSelector,
  useViewActions
} from "store";
import {
  ExtractRouteParams,
  PermissionValueType,
  RoutePath,
  RoutesKeys,
  RoutesValues,
  UserPermissionType
} from "store/types";
import { isRoleAnalyst } from "utils/user";

import { ReactComponent as AdminToolsIcon } from "../../assets/icons/admin-tools-sidebar.svg";
import { ReactComponent as AssetBenchmark } from "../../assets/icons/asset-benchmark.svg";
import { ReactComponent as BulletedList } from "../../assets/icons/bulleted-list-outline.svg";
import { ReactComponent as Cloud } from "../../assets/icons/cloud.svg";
import { ReactComponent as MyPortfolio } from "../../assets/icons/my-portfolio.svg";
import { ReactComponent as PortfolioIcon } from "../../assets/icons/portfolio-overview.svg";
import { ReactComponent as LogoTitle } from "../../assets/icons/sidebar-altius-title.svg";
import { ReactComponent as TaskIcon } from "../../assets/icons/task.svg";
import { AdminToolsMenu } from "./Sidebar-components/Admin-tools-menu";
import { FreemiumUpgradeCard } from "./Sidebar-components/Freemium-upgrade-card";
import { SectionHeader } from "./Sidebar-components/Section-header";

interface SidebarProps {
  defaultState?: "open" | "close";
}

export interface LinkParams {
  route: RoutesKeys;
  path: RoutesValues;
  title: string;
  enabled?: boolean;
}

interface menuItems {
  availableRoles: RoleName[];
  route: RoutesKeys;
  title: string;
  enabled?: boolean;
  icon?: React.ReactNode;
  option?: string;
  permission?: PermissionValueType;
  params?: ExtractRouteParams<RoutePath<RoutesKeys>>;
}

interface MenuSection {
  title: string;
  items: menuItems[];
}

export const Sidebar: React.FC<SidebarProps> = ({ defaultState = "close" }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const initialScreen = defaultState === "close" ? false : true;
  const isBenchmarkEnabled = useBenchmarkSelector();
  const {
    allow_dataroom: dataRooms,
    show_plaid: isPlaidEnabled,
    allow_social: isSocialEnabled,
    allow_news: isDashboardEnabled,
    allow_account_invitation,
    external_report,
    enable_user_management,
    is_freemium,
    upgraded_from_freemium,
    show_smartsheet,
    show_ai_report_generator,
    show_ai_intelligence,
    enable_portfolio_construction
  } = useAccountSelector() || {};
  const cloudIntegrations = dataRooms || isPlaidEnabled;
  const isReportEnabled = useReportsSelector();
  const user = useUserSelector();
  const has_shared_deals = user.has_shared_deals;

  const userPermissions = useUserPermissions();
  const investors = useInvestorsSelector();
  const savedInvestorId = usePortfolioConstructSelector();
  const investorId =
    savedInvestorId && investors.some((i) => i.id === savedInvestorId) ? savedInvestorId : investors[0]?.id;

  const [isFullSize, setIsFullSize] = useState(initialScreen);
  const role = useUserRoleSelector();
  const { getReportGeneratorHashAction } = useAuthActions();
  const { setSidebarStateAction } = useViewActions();
  const sidebarState = useSidebarStateSelect();

  const { action: getReportGeneratorHash, loading: reportGeneratorLoading } = useActionHook(
    getReportGeneratorHashAction,
    {
      onSuccess: (uuid) => {
        const domain = import.meta.env.VITE_NODE_ENV === "production" ? "altius.capital" : "altius.finance";
        window.open(`https://intelligence.${domain}/?sid=${uuid}`, "_blank");
      }
    }
  );

  const onCloseSidebar = () => {
    setIsFullSize(false);
    setAdminToolsOpen(false);
    // setAdditionReportsOpen(false);
  };

  const logoClick = () => {
    if (isRoleAnalyst({ role }) && is_freemium) {
      navigate({ route: "freemium" });
      onCloseSidebar();
      return;
    }
    if (isRoleAnalyst({ role }) && upgraded_from_freemium) {
      navigate({ route: "freemium" });
      onCloseSidebar();
      return;
    }
    if (isDashboardEnabled) {
      navigate({ route: "dashboard" });
      onCloseSidebar();
      return;
    }
    navigate({ route: "myDeals" });
    onCloseSidebar();
  };

  const iconHoverStyles = { backgroundColor: "#0f3a54", borderRadius: "8px" };
  const menuItemHeight = "28px";
  const defaultIconButtonStyles = { width: "30px", height: menuItemHeight, padding: 0 };

  const menuItemStyles = {
    borderRadius: "8px",
    width: "100%",
    height: menuItemHeight,
    ":hover": { background: "#0f3a54" },
    display: "flex",
    alignItems: "center",
    padding: "3px 7px 3px 7px",
    color: "#92a7bc",
    fontSize: "15px",
    cursor: "pointer"
  };
  const [adminToolsOpen, setAdminToolsOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");

  // Open sidebar only after fresh login (not on session restore)
  useEffect(() => {
    if (sidebarState) {
      setIsFullSize(true);
      setSidebarStateAction(false);
    }
  }, [sidebarState, setSidebarStateAction]);

  useEffect(() => {
    const routeOptions: Record<string, string> = {
      [Routes.myDeals]: "my deals",
      [Routes.deal]: "my deals",
      [Routes.mySecurities]: "my securities",
      [Routes.benchmark]: "benchmark",
      [Routes.quickView]: "smart sheet",
      [Routes.taskManagement]: "task management",
      [Routes.dataRooms]: "data rooms",
      [Routes.monitoring]: "data operations",
      [Routes.monitoringData]: "additional reports",
      [Routes.comparison]: "comparison",
      [Routes.externalReport]: "external reports",
      [Routes.investorsList]: "admin tools",
      [Routes.measures]: "admin tools",
      [Routes.characteristics]: "admin tools",
      [Routes.assets]: "admin tools",
      [Routes.plaid]: "admin tools",
      [Routes.snapshot]: "portfolio analysis",
      [Routes.timeline]: "portfolio analysis",
      [Routes.classic]: "portfolio analysis",
      [Routes.cashflow]: "portfolio analysis",
      [Routes.social]: "Profile Network",
      [Routes.reportGenerator]: "report generator",
      [Routes.dashboard]: "dashboard",
      [Routes.newsFeed]: "news feed",
      [Routes.documents]: "documents",
      [Routes.usersManagement]: "usersManagement",
      [Routes.users]: "usersManagement",
      [Routes.teams]: "usersManagement",
      [Routes.portfolioConstructionInvestor]: "portfolio construction",
      [Routes.workspace]: "explore"
    };

    const selectedOption = Object.keys(routeOptions).find((route) => matchPath(pathname, route));

    if (selectedOption) {
      setSelectedOption(routeOptions[selectedOption]);
    } else {
      setSelectedOption("");
    }
  }, [pathname]);

  const dataroomRoute = useMemo(() => {
    if (
      role?.name === RoleName.TeamViewer ||
      (role?.name === RoleName.Analyst &&
        userPermissions?.["cloud_integrations"] &&
        userPermissions["cloud_integrations"] === UserPermissionType.VIEWER &&
        !is_freemium) ||
      (role?.name === RoleName.Analyst &&
        userPermissions?.["cloud_integrations"] &&
        userPermissions["cloud_integrations"] === UserPermissionType.EDITOR &&
        !is_freemium)
    ) {
      return "dataRoomsAllFiles";
    }

    return "dataRooms";
  }, [userPermissions, is_freemium, role]);

  // Section-based menu structure
  const menuSections: MenuSection[] = useMemo(() => {
    const portfolioSection: MenuSection = {
      title: "PORTFOLIO",
      items: [
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
          route: "dashboard" as RoutesKeys,
          enabled: isDashboardEnabled,
          title: "Dashboard",
          icon: <DashboardOutlinedIcon sx={{ fill: "#92a7bc", width: "17px", height: "17px" }} />,
          option: "dashboard"
        },
        {
          availableRoles: [
            RoleName.Admin,
            RoleName.Analyst,
            RoleName.TeamViewer,
            RoleName.Guest,
            RoleName.Viewer,
            RoleName.Investor
          ],
          route: "myDeals",
          enabled: has_shared_deals === null ? true : has_shared_deals,
          title: "My Deals",
          icon: <MyPortfolio style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "my deals",
          ...(userPermissions?.["deals"] && { permission: userPermissions["deals"] })
        },
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer],
          route: "newsFeed" as RoutesKeys,
          enabled: true,
          title: "News Feed",
          icon: <NewspaperIcon sx={{ fill: "#92a7bc", width: "17px", height: "17px" }} />,
          option: "news feed"
        },
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
          route: dataroomRoute as RoutesKeys,
          enabled: cloudIntegrations,
          title: "Cloud Integration",
          icon: <Cloud style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "data rooms",
          ...(userPermissions?.["cloud_integrations"] && { permission: userPermissions["cloud_integrations"] })
        }
      ]
    };

    const workspaceSection: MenuSection = {
      title: "WORKSPACE",
      items: [
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
          route: "workspaceAllFiles",
          enabled: true,
          title: "Explore",
          icon: <GlobalWorkspaceSidebarIcon style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "explore"
        },
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
          route:
            userPermissions?.["deal_metrics"] && userPermissions["deal_metrics"] === UserPermissionType.NO_ACCESS
              ? "fileRepository"
              : "monitoringData",
          enabled: isReportEnabled,
          title: "Data Operations",
          icon: <EqualizerIcon style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "data operations"
        },
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst],
          route: "quickView",
          enabled: show_smartsheet,
          title: "Smart Sheet",
          icon: <BulletedList style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "smart sheet",
          ...(userPermissions?.["smart_sheet"] && { permission: userPermissions["smart_sheet"] })
        },
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
          route: "taskManagement",
          enabled: true,
          title: "Task Manager",
          icon: <TaskIcon style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "task management",
          ...(userPermissions?.["task_manager"] && { permission: userPermissions["task_manager"] })
        }
      ]
    };

    const analyticsSection: MenuSection = {
      title: "ANALYTICS",
      items: [
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Investor],
          route: "reports",
          enabled: isReportEnabled,
          title: "Portfolio Analysis",
          icon: <PortfolioIcon style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "portfolio analysis",
          ...(userPermissions?.["portfolio_analysis"] && { permission: userPermissions["portfolio_analysis"] })
        },
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
          route: "benchmark",
          enabled: isBenchmarkEnabled,
          title: "Benchmark",
          icon: <AssetBenchmark style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "benchmark",
          ...(userPermissions?.["benchmark"] && { permission: userPermissions["benchmark"] })
        },
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst],
          route: "portfolioConstructionInvestor",
          enabled: Boolean(investorId) && enable_portfolio_construction,
          params: { investor_id: investorId, tab: "allocation" },
          title: "Portfolio Construction",
          icon: <PortfolioOverviewIcon style={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "portfolio construction",
          ...(userPermissions?.["portfolio_construction"] && { permission: userPermissions["portfolio_construction"] })
        },
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst],
          route: "reportGenerator",
          enabled: show_ai_report_generator,
          title: "Report Generator",
          icon: <ReportGeneratorIcon sx={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "report generator"
        },
        ...(external_report
          ? [
              {
                availableRoles: [RoleName.Admin, RoleName.Analyst],
                route: "externalReport" as RoutesKeys,
                enabled: true,
                title: "External Reports",
                icon: <SsidChartIcon sx={{ color: "#92a7bc", width: "14px", height: "14px" }} />,
                option: "external reports",
                ...(userPermissions?.["external_reports"] && { permission: userPermissions["external_reports"] })
              }
            ]
          : []),
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
          route: "comparison",
          enabled: import.meta.env.VITE_NODE_ENV !== "production",
          title: "Comparison",
          icon: <CompareIcon sx={{ color: "#92a7bc", width: "15px", height: "15px" }} />,
          option: "comparison"
        }
      ]
    };

    const networkSection: MenuSection = {
      title: "NETWORK",
      items: [
        {
          availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
          route: "social",
          enabled: isSocialEnabled,
          title: "Social Network",
          icon: <PeopleAltOutlinedIcon sx={{ color: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "Profile Network",
          ...(userPermissions?.["social"] && { permission: userPermissions["social"] })
        },
        {
          availableRoles: [RoleName.Admin],
          route: "usersManagement",
          enabled: enable_user_management,
          title: "Entity Management",
          icon: <ManageAccountsIcon sx={{ fill: "#92a7bc", width: "14px", height: "14px" }} />,
          option: "usersManagement"
        }
      ]
    };

    const isItemVisible = (item: menuItems) =>
      Boolean(item.enabled) &&
      Boolean(role?.name && item.availableRoles.includes(role.name as RoleName)) &&
      item.permission !== UserPermissionType.NO_ACCESS;

    // Keep only visible items, then drop sections that end up with no items
    // so the section header is never rendered without sub-items below it.
    return [portfolioSection, workspaceSection, analyticsSection, networkSection]
      .map((section) => ({ ...section, items: section.items.filter(isItemVisible) }))
      .filter((section) => section.items.length > 0);
  }, [
    isDashboardEnabled,
    has_shared_deals,
    userPermissions,
    cloudIntegrations,
    dataroomRoute,
    isReportEnabled,
    show_smartsheet,
    isBenchmarkEnabled,
    investorId,
    enable_portfolio_construction,
    show_ai_report_generator,
    isSocialEnabled,
    enable_user_management,
    external_report,
    role
  ]);

  return (
    <>
      <ClickAwayListener onClickAway={() => onCloseSidebar()}>
        <Box
          sx={{
            transition: "width 0.5s ease",
            zIndex: "1001", // 1001 is higher than PDF tagger tags table (zIndex: 1000).
            height: "100%",
            position: "absolute",
            backgroundColor: "#071f35",
            width: "64px",
            boxShadow: "0px 1px 13px rgba(10, 49, 66, 0.7)",
            ...(isFullSize && {
              width: "220px"
            }),
            "&:hover .toggle-arrow": {
              opacity: 1
            }
          }}
        >
          {isFullSize ? (
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <Box
                sx={{
                  padding: "20px 15px 0 15px",
                  whiteSpace: "nowrap",
                  width: "100%",
                  overflow: "hidden",
                  flexShrink: 0
                }}
              >
                <Box sx={{ cursor: "pointer", display: "inline-block" }} onClick={() => logoClick()}>
                  <Logo style={{ width: "25px", height: "26px", marginRight: "7px" }} />
                  <LogoTitle style={{ height: "26px", width: "80px" }} />
                </Box>
                <Box
                  className="toggle-arrow"
                  onClick={() => setIsFullSize(false)}
                  sx={{
                    display: "flex",
                    top: "40%",
                    right: "-12px",
                    position: "absolute",
                    width: "24px",
                    height: "24px",
                    borderRadius: "9999px",
                    backgroundColor: "#496682",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#0f3a54"
                    }
                  }}
                >
                  <KeyboardArrowLeftIcon sx={{ fontSize: "15px", fill: "#92a7bc" }} />
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "20px 15px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": { backgroundColor: "#496682", borderRadius: "4px" },
                  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" }
                }}
              >
                {menuSections.map((section, sectionIndex) => (
                  <Box key={sectionIndex}>
                    <SectionHeader title={section.title} />
                    <Stack sx={{ gap: "8px" }}>
                      {section.items.map(({ route, params, title, icon, option }, itemIndex) => (
                        <Box
                          key={itemIndex}
                          onClick={() => {
                            navigate({ route: route, ...(params && { params }) });
                            onCloseSidebar();
                          }}
                          sx={{
                            ...(selectedOption === option && {
                              backgroundColor: "#496682",
                              borderRadius: "8px",
                              color: "#ffffff"
                            }),
                            ...menuItemStyles
                          }}
                        >
                          <Stack
                            sx={{
                              minWidth: "27px",
                              alignItems: "center",
                              ...(selectedOption === option && {
                                "& svg": { fill: "#ffffff !important", color: "#ffffff !important" }
                              })
                            }}
                          >
                            {icon}
                          </Stack>
                          <Typography
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              fontSize: "15px",
                              color: selectedOption === option ? "#ffffff" : "#92a7bc"
                            }}
                          >
                            {title}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
                {Boolean(show_ai_intelligence) && (
                  <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap="8px"
                      onClick={getReportGeneratorHash}
                      sx={{
                        ...menuItemStyles,
                        ...(reportGeneratorLoading && { pointerEvents: "none", opacity: 0.7 })
                      }}
                    >
                      {reportGeneratorLoading ? (
                        <CircularProgress size={14} sx={{ color: "#92a7bc" }} />
                      ) : (
                        <PsychologyReverse sx={{ fill: "#92a7bc", width: "14px", height: "14px" }} />
                      )}
                      <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", fontSize: "15px", color: "#92a7bc" }}>
                        Data Intelligence
                      </Typography>
                    </Stack>
                  </RoleComponentGuard>
                )}
              </Box>
              {is_freemium && (
                <Box sx={{ padding: "10px", marginBottom: "62px" }}>
                  <FreemiumUpgradeCard onUpgrade={() => setUpgradeModalOpen(true)} />
                </Box>
              )}
              <AdminToolsMenu
                setAdminToolsOpen={setAdminToolsOpen}
                // setAdditionReportsOpen={setAdditionReportsOpen}
                adminToolsOpen={adminToolsOpen}
                selectedOption={selectedOption}
                onCloseSidebar={onCloseSidebar}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                height: "100%",
                p: "20px 0 0 0"
              }}
            >
              <Box onClick={() => logoClick()} sx={{ padding: "0 15px", flexShrink: 0 }}>
                <Logo style={{ width: "25px", height: "26px", cursor: "pointer" }} />
              </Box>
              <Box
                className="toggle-arrow"
                onClick={() => setIsFullSize(true)}
                sx={{
                  display: "flex",
                  top: "40%",
                  right: "-12px",
                  position: "absolute",
                  width: "24px",
                  height: "24px",
                  borderRadius: "9999px",
                  backgroundColor: "#496682",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#0f3a54"
                  }
                }}
              >
                <KeyboardArrowRightIcon sx={{ fontSize: "15px", fill: "#92a7bc" }} />
              </Box>
              <Stack
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  gap: "24px",
                  alignItems: "center",
                  py: "20px",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none"
                }}
              >
                {menuSections.map((section, sectionIndex) => (
                  <Box key={sectionIndex}>
                    <Box sx={{ visibility: "hidden" }}>
                      <SectionHeader title={section.title} />
                    </Box>
                    <Stack sx={{ alignItems: "center", gap: "8px" }}>
                      {section.items.map(({ route, params, title, icon, option }, itemIndex) => (
                        <MaterialTooltip key={itemIndex} isShow placement="right" title={title}>
                          <IconButton
                            onClick={() => {
                              navigate({ route, ...(params && { params }) });
                              onCloseSidebar();
                            }}
                            sx={{
                              "&:hover": { ...iconHoverStyles },
                              ...defaultIconButtonStyles,
                              ...(selectedOption === option && {
                                backgroundColor: "#496682",
                                borderRadius: "8px",
                                "& svg": { fill: "#ffffff !important", color: "#ffffff !important" }
                              })
                            }}
                          >
                            {icon}
                          </IconButton>
                        </MaterialTooltip>
                      ))}
                    </Stack>
                  </Box>
                ))}
                {Boolean(show_ai_intelligence) && (
                  <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                    <MaterialTooltip isShow placement="right" title="Data Intelligence">
                      <IconButton
                        onClick={getReportGeneratorHash}
                        disabled={reportGeneratorLoading}
                        sx={{
                          "&:hover": { ...iconHoverStyles },
                          ...defaultIconButtonStyles
                        }}
                      >
                        {reportGeneratorLoading ? (
                          <CircularProgress size={14} sx={{ color: "#92a7bc" }} />
                        ) : (
                          <PsychologyReverse sx={{ fill: "#92a7bc", width: "14px", height: "14px" }} />
                        )}
                      </IconButton>
                    </MaterialTooltip>
                  </RoleComponentGuard>
                )}
              </Stack>
              <RoleComponentGuard availableRoles={[RoleName.Admin]}>
                <Stack gap="20px" width="100%" alignItems="center">
                  {allow_account_invitation && <AccountInvite />}
                  <MaterialTooltip isShow placement="top" title={"Admin Tools"}>
                    <Box
                      onClick={() => {
                        setIsFullSize(true);
                        setAdminToolsOpen(!adminToolsOpen);
                        // setAdditionReportsOpen(false);
                      }}
                      sx={{
                        display: "flex",
                        height: "60px",
                        width: "100%",
                        border: "1px solid #496682",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <IconButton
                        sx={{
                          "&:hover": { ...iconHoverStyles },
                          ...defaultIconButtonStyles,
                          ...(selectedOption === "admin tools" && { backgroundColor: "#496682", borderRadius: "8px" })
                        }}
                      >
                        <AdminToolsIcon
                          style={{ fill: selectedOption === "admin tools" ? "#ffffff" : "#92a7bc", width: "14px" }}
                        />
                      </IconButton>
                    </Box>
                  </MaterialTooltip>
                </Stack>
              </RoleComponentGuard>
            </Box>
          )}
        </Box>
      </ClickAwayListener>
      {is_freemium && (
        <UpgradePlanModal isOpen={upgradeModalOpen} manualUpgrade onClose={() => setUpgradeModalOpen(false)} />
      )}
    </>
  );
};
